import { useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "components/primitives/Grid";
import HotspotBadge from "components/HotspotBadge";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import AthleteProfileChartAdvancedOptions from "./AthleteProfileChartAdvancedOptions";
import { dataVisualizationOptions, DataVisualizationType } from "components/charting/config/chartTypes";
import useDataVisualizationType from "components/charting/hooks/useDataVisualizationType";
import useSetDataVisualizationType from "components/charting/hooks/useSetDataVisualizationType";
import AthleteProfileChartControls from "./AthleteProfileChartControls";
import AthleteProfileDataVisualizationSelector from "../AthleteProfileDataVisualizationSelector";
import useChartQueryParams from "components/charting/hooks/useChartQueryParams";
import TooltipHelper from "components/TooltipHelper";
import * as Components from "./AthleteProfileChart.components";
import { useTranslation } from "react-i18next";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import Typography from "components/Typography";
import Div from "components/primitives/Div";
import { athlete1RMV2Enabled } from "config/features";
import useAthleteProfileChartVelocityHotspots from "./hooks/useAthleteProfileChartVelocityHotspots";

function AthleteProfileChart() {
  const dataVisualizationType = useDataVisualizationType();
  const setDataVisualizationType = useSetDataVisualizationType();

  useChartQueryParams();
  const { t } = useTranslation();
  const hotspots = useAthleteProfileChartVelocityHotspots();

  const buttons = useMemo(
    () =>
      dataVisualizationOptions.map(({ titleKey, type, icon, descriptionKey }) => (
        <ThemeProvider2024 key={type}>
          <TooltipHelper title={t(descriptionKey)} placement="right">
            <Button
              aria-label={t("chart_types.select_chart_aria_label", { title: t(titleKey) })}
              onClick={() => setDataVisualizationType(type)}
              sx={(theme) => ({ borderRadius: "4px", fontSize: 32, padding: theme.spacing(1, 0) })}
              variant={dataVisualizationType === type ? "contained" : undefined}
            >
              <HotspotBadge variant="dot" invisible={!hotspots[type]}>
                {icon}
              </HotspotBadge>
            </Button>
          </TooltipHelper>
        </ThemeProvider2024>
      )),
    [t, dataVisualizationType, setDataVisualizationType, hotspots]
  );

  const headers = useMemo(() => {
    return {
      [DataVisualizationType.LINE_CHART]: {
        title: t("chart_types.line_chart"),
      },
      [DataVisualizationType.PROFILING_CHART]: {
        title: t("global.profiling_label"),
        subtitle: t("athlete_profile.profiling_vis_type_subtitle"),
      },
      [DataVisualizationType.SET_COMPARISON_CHART]: {
        title: t("athlete_profile.set_comparison_vis_type_title"),
      },
      [DataVisualizationType.ONE_REP_MAX_CHART]: {
        title: t("athlete_profile.one_rep_max_vis_type_title"),
        subtitle: athlete1RMV2Enabled
          ? t("athlete_profile.one_rep_max_vis_type_subtitle_v2")
          : t("athlete_profile.one_rep_max_vis_type_subtitle"),
      },
      [DataVisualizationType.MEASUREMENTS_TABLE]: {
        title: t("chart_types.measurements_table_description"),
      },
    } as Record<DataVisualizationType, { title: string; subtitle?: string }>;
  }, [t]);

  const currentHeader = headers[dataVisualizationType];

  return (
    <Box sx={{ height: "100%", display: "flex" }}>
      <Components.SideMenuStyledBox
        display="flex"
        justifyContent="stretch"
        flexDirection="column"
        p={1}
        pt={1.5}
        pb={1.5}
        position="relative"
      >
        <Grid
          container
          sx={{
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: "column",
            flex: 1,
          }}
        >
          <Grid>
            <ButtonGroup
              size="small"
              orientation="vertical"
              disableElevation
              color="primary"
              sx={(theme) => ({
                "& .MuiButton-contained": {
                  border: `1px solid ${theme.palette.primary.main}`,
                  "& + .MuiButton-outlined": {
                    borderTop: `1px solid ${theme.palette.primary.main}`,
                  },
                },
              })}
            >
              {buttons}
            </ButtonGroup>
          </Grid>
          <Grid>
            <AthleteProfileChartAdvancedOptions />
          </Grid>
        </Grid>
      </Components.SideMenuStyledBox>
      <Components.BorderLeftBox display="flex" flexDirection="column" flex={1} gap={2}>
        <ThemeProvider2024>
          <Div sx={{ padding: "12px 16px 0 16px", display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="TextXL" component="h2" sx={{ fontWeight: "fontWeightMedium" }}>
              {currentHeader?.title}
            </Typography>
            {currentHeader?.subtitle ? (
              <Typography variant="TextS" sx={{ color: "grey.120" }}>
                {currentHeader.subtitle}
              </Typography>
            ) : null}
          </Div>
        </ThemeProvider2024>
        <Components.BorderBottomBox sx={{ borderBottom: `none`, padding: "0 16px" }}>
          <AthleteProfileChartControls />
        </Components.BorderBottomBox>
        <AthleteProfileDataVisualizationSelector />
      </Components.BorderLeftBox>
    </Box>
  );
}

export default AthleteProfileChart;
